<template>
  <v-app>
    <section>
      <h2>Edit Notification '{{ notification.title }}'</h2>

      <v-snackbar
        v-model="snackbar"
        color="success"
        top
        right
        :timeout=3000
      >
        <v-icon dark left>mdi-check</v-icon>
        Notification has been created. Redirection to the notification page in {{ countDown }}
        <v-btn
          dark
          text
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </v-snackbar>

      <v-dialog
        v-model="sendLoading"
        hide-overlay
        persistent
        width="300"
      >
        <v-card
          color="primary"
          dark
        >
          <v-card-text>
            Sending notification
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-0"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-snackbar
        v-model="snackbarError"
        color="error"
        top
        right
        :timeout=3000
      >
        <v-icon dark left>mdi-check</v-icon>
        An error has occured.
        <v-btn
          dark
          text
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </v-snackbar>

      <!-- STEPPER -->
      <v-stepper class="mt-5 mb-10 elevation-0" :value="step" v-model="step">
        <v-stepper-header class="elevation-0">
          <v-stepper-step :complete="step > 1" step="1">Create</v-stepper-step>
          <v-divider/>
          <v-stepper-step step="2">Select audience</v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <!-- STEP 1 CONTENT -->
          <v-stepper-content step="1">

            <v-form>
              <!-- COMMON PANEL -->
              <v-expansion-panels
                v-model="panel"
                class="panel pa-1"
                multiple
              >
                <v-expansion-panel>
                  <v-expansion-panel-header>Common</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-text-field
                      label="Title*"
                      hide-details
                      class="input"
                      :value="notification.title"
                      v-model="notification.title"
                      :rules="rules"
                    />
                    <div class="notification-type">
                      <v-select
                        :items="notificationType"
                        item-text="iconLabel"
                        item-value="iconValue"
                        v-model="notificationTypeName"
                        label="Notification icon*"
                        class="mt-8 mb-3 mr-8"
                        @change="setNotificationUrl"
                        :rules="rules"
                      />
                      <v-img
                        v-if="notificationTypeName"
                        contain
                        aspect-ratio="1"
                        max-width="60"
                        max-height="60"
                        class="notification-icon"
                        :src="notification.iconUrl"
                      />
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- langage PANELS -->
                <v-expansion-panel v-for="(translation, id) in notification.translations" :key="id">
                  <v-expansion-panel-header>{{ getLangageString(translation.langageId) }}</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-text-field
                      label="Notification Title*"
                      class="input"
                      :value="translation.title"
                      v-model="translation.title"
                      :rules="rules"
                    />
                    <v-textarea
                      label="Notification Content*"
                      class="input"
                      :value="translation.description"
                      v-model="translation.description"
                      :rules="rules"
                    />
                    <v-btn
                      @click="deleteTranslation(id)"
                      width="100"
                      color="error"
                      class="mt-3"
                      dark
                    >
                      Delete
                      <v-icon dark right>mdi-trash-can</v-icon>
                    </v-btn>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- NEW TRANSLATION PANEL -->
                <v-expansion-panel>
                  <v-expansion-panel-header>Add new translation...</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-radio-group v-model="langageId" :mandatory="false">
                      <v-radio :disabled="langageIdUsed.includes(1)" label="English (en)" :value=1 />
                      <v-radio :disabled="langageIdUsed.includes(2)" label="Danish (da)" :value=2 />
                      <v-radio :disabled="langageIdUsed.includes(3)" label="German (de)" :value=3 />
                      <v-radio :disabled="langageIdUsed.includes(4)" label="Spanish (es)" :value=4 />
                      <v-radio :disabled="langageIdUsed.includes(5)" label="French (fr)" :value=5 />
                      <v-radio :disabled="langageIdUsed.includes(6)" label="Hungarian (hu)" :value=6 />
                      <v-radio :disabled="langageIdUsed.includes(7)" label="Italian (it)" :value=7 />
                      <v-radio :disabled="langageIdUsed.includes(8)" label="Dutch (nl)" :value=8 />
                      <v-radio :disabled="langageIdUsed.includes(9)" label="Polish (po)" :value=9 />
                    </v-radio-group>
                    <v-btn
                      width="100"
                      color="primary"
                      @click="addTranslation"
                      class="mt-3"
                    >
                      Add
                      <v-icon dark right>mdi-plus</v-icon>
                    </v-btn>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-form>

            <v-btn
              width="250"
              class="btn mt-6 mb-1 ml-1"
              color="error"
              @click="redirectToBack"
            >
              <v-icon dark left size="large">mdi-arrow-left</v-icon>
              Cancel
            </v-btn>
            <v-btn
              :disabled="!isFormValid"
              width="250"
              class="btn mt-6 ml-3 mb-1"
              color="primary"
              @click="saveNotification"
            >
              Save as draft
              <v-icon dark right size="large">mdi-content-save</v-icon>
            </v-btn>
            <v-btn
              :disabled="!isFormValid"
              width="250"
              class="btn mt-6 ml-3 mb-1"
              color="primary"
              @click="goToStep2"
            >
              Select audience
              <v-icon dark right size="large">mdi-arrow-right</v-icon>
            </v-btn>
          </v-stepper-content>

          <!-- STEP 2 CONTENT -->
          <v-stepper-content step="2">

            <v-radio-group v-model="radioGroup" class="mb-6">
              <v-radio
                v-for="index in 2"
                :key="index"
                :label="radioLabel[index - 1]"
                :value="index"
              />
            </v-radio-group>

            <!-- INCIDENT REPORT SECTION -->
            <template v-if="radioGroup === 1">
              <p>Send notification to Users that had at least one connected rover at the following incident date:</p>

              <!-- DATE PICKER -->
              <v-row class="mt-5">
                <v-col cols="6" sm="6">
                  <v-menu
                    ref="menu"
                    v-model="datePicker"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    :return-value.sync="time"
                    transition="scale-transition"
                    offset-y
                    max-width="300px"
                    min-width="300px"
                  >
                    <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="date"
                      label="Select date"
                      prepend-icon="mdi-calendar-month"
                      readonly
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="date" @input="menu1 = false"/>
                  </v-menu>
                </v-col>
                <v-spacer/>
                <v-col cols="6" sm="6">
                  <v-menu
                    ref="menu"
                    v-model="timePicker"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    :return-value.sync="time"
                    transition="scale-transition"
                    offset-y
                    max-width="300px"
                    min-width="300px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="time"
                        label="Select time"
                        prepend-icon="mdi-clock-outline"
                        readonly
                        v-on="on"
                      />
                    </template>
                    <v-time-picker
                      v-if="timePicker"
                      v-model="time"
                      full-width
                      @click:minute="$refs.menu.save(time)"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="12">
                  <v-select
                    v-model="selectedCountries"
                    :items="countries"
                    label="Filter by country"
                    prepend-icon="mdi-earth"
                    chips
                    multiple
                    :rules="rulesCountries"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item ripple @click="toggle">
                        <v-list-item-action>
                          <v-icon :color="selectedCountries.length > 0 ? 'primary' : ''">
                            {{ countrySelectAllIcon }}
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>
                            Select All
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-2"/>
                    </template>
                    <template v-slot:append-item>
                      <v-divider class="mb-2"></v-divider>
                      <v-list-item disabled>
                        <v-list-item-avatar color="grey lighten-3">
                          <v-icon>mdi-earth</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ `${selectedCountries.length} / ${countries.length} selected` }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>
                <v-col>
                  <v-btn
                    @click="searchUsers"
                    class="btn"
                    color="primary"
                  >
                    <v-icon dark left>mdi-account-supervisor</v-icon>
                    Search users connected at the time...
                  </v-btn>
                </v-col>
              </v-row>
            </template>

            <!-- SEND TO GROUP SECTION -->
            <v-card
              v-if="radioGroup === 2"
              class="ml-1 mr-1"
            >
              <v-card-title>
                {{ userGroups.length }} User Groups
                <v-spacer/>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search user group by title"
                  single-line
                  hide-details
                />
              </v-card-title>

              <v-data-table
                v-if="userGroups"
                v-model="notification.typeValue"
                :headers="headers"
                :items="userGroups"
                item-key="userGroupId"
                :search="search"
                :loading="isLoading"
                :single-select="singleSelect"
                show-select
                loading-text="Loading... Please wait"
              >
                <template v-slot:top>
                  <v-switch
                    v-model="singleSelect"
                    label="Single select"
                    class="pa-3"
                  />
                </template>
                <template v-slot:item.title="{ item }">
                  <v-item-group>
                    <v-item dark>
                      <div>{{ item.title ? item.title : '--' }}</div>
                    </v-item>
                  </v-item-group>
                </template>
                <template v-slot:item.usercount="{ item }">
                  <v-item-group>
                    <v-item dark>
                      <div>{{ item.usercount ? item.usercount : '--' }}</div>
                    </v-item>
                  </v-item-group>
                </template>
                <template v-slot:item.action="{ item }">
                  <v-chip
                    class="mr-2"
                    color="blue"
                    dark
                    @click="modalSendNotification = true, userGroupId = item.userGroupId"
                  >
                    <v-icon
                      size="large"
                      color="white"
                      class="mr-2"
                    >
                      mdi-send
                    </v-icon>
                    Send to this group
                  </v-chip>
                </template>
                <template>
                  <v-data-table item-key="title" class="elevation-1" loading loading-text="Loading... Please wait"/>
                </template>
                <template v-slot:no-data>
                  <p class="mt-4">User group unavailable</p>
                </template>
              </v-data-table>
            </v-card>

            <v-btn
              @click="cancel"
              width="250"
              class="btn mt-8 mb-1 ml-1"
              color="error"
            >
              <v-icon dark left>mdi-arrow-left</v-icon>
              Back
            </v-btn>

            <v-btn
              v-if="radioGroup === 2"
              :disabled="!notification.typeValue.length > 0"
              @click="modalSendNotification = true"
              width="250"
              class="btn mt-8 mb-1 ml-2"
              color="primary"
            >
              {{ `Send to ${notification.typeValue.length > 0 ? notification.typeValue.length : ''} group${notification.typeValue.length > 1 ? 's': ''}` }}
              <v-icon dark right>mdi-send</v-icon>
            </v-btn>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </section>

    <!-- SELECT TEMPLATE PANEL -->
    <template>
      <v-row justify="center">
        <v-dialog v-model="modalTemplate" scrollable max-width="600px">
          <v-card>
            <v-card-title>Please select the template to use for this new notification:</v-card-title>
            <v-divider/>
            <v-card-text style="height: 300px;">
              <v-radio-group v-model="selectedTemplateId" column>
                <v-radio
                  v-for="(template, id) in templates"
                  :key="id"
                  :label="template.title"
                  :value="template.notificationTemplateId"
                />
              </v-radio-group>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
              <v-btn color="blue darken-1" :disabled="selectedTemplateId === null" text @click="getTemplate">Choose</v-btn>
              <v-btn color="blue darken-1" text @click="skip">Skip</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </template>

    <!-- SEND INCIDENT REPORT NOTIFICATION PANEL -->
    <template>
      <v-row justify="center">
        <v-dialog v-model="modalIncidentReport" max-width="700px">
          <v-card class="mx-auto align-end" v-if="userGroupsError.usergroup">
            <v-card-title>Send Incident Report '{{ notification.title }}' to the {{ userGroupsError.totalUsers }} matching users?</v-card-title>
            <v-card-subtitle class="mt-1 mb-3">Once the notification is sent, you will not be able to edit it.</v-card-subtitle>
            <v-card-actions>
              <v-spacer/>
              <v-btn
                color="error"
                class="mr-1 mb-2"
                @click="modalIncidentReport = false"
              >
                <v-icon
                  size="large"
                  color="white"
                  class="ml-2"
                  left
                >
                   mdi-arrow-left
                </v-icon>
                Cancel
              </v-btn>
              <v-btn
                color="blue darken-1"
                dark
                class="mr-5 mb-2"
                @click="sendNotification(userGroupsError.usergroup.userGroupId)"
              >
                <v-icon
                  size="large"
                  color="white"
                  class="ml-2"
                  left
                >
                  mdi-send
                </v-icon>
                Send to {{ userGroupsError.totalUsers }} users
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card class="mx-auto align-end" v-else>
            <v-card-title>No user match with time and countries</v-card-title>
            <v-card-actions>
              <v-spacer/>
              <v-btn
                color="blue darken-1"
                dark
                class="mr-5 mb-2"
                @click="modalIncidentReport = false"
              >
                <v-icon
                  size="large"
                  color="white"
                  class="ml-2"
                  left
                >
                  mdi-arrow-left
                </v-icon>
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </template>

    <!-- SEND NOTIFICATION MANUALLY CONFIRMATION PANEL -->
    <template>
      <v-row justify="center">
        <v-dialog v-model="modalSendNotification" max-width="700px">
          <v-card class="mx-auto align-end">
            <v-card-title>Send notification '{{ notification.title }}' ?</v-card-title>
            <v-card-subtitle class="mt-1 mb-3">Notification will be sent immediately.</v-card-subtitle>
            <v-card-actions>
              <v-spacer/>
              <v-btn
                color="error"
                class="mr-1 mb-2"
                @click="modalSendNotification = false"
              >
                <v-icon
                  size="large"
                  color="white"
                  class="ml-2"
                  left
                >
                  mdi-arrow-left
                </v-icon>
                Cancel
              </v-btn>
              <v-btn
                color="blue darken-1"
                dark
                class="mr-5 mb-2"
                @click="sendNotification()"
              >
                Send
                <v-icon
                  size="large"
                  color="white"
                  class="ml-2"
                  left
                >
                  mdi-send
                </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </template>

    <!-- EXIT CONFIRMATION PANEL -->
    <template>
      <v-row justify="center">
        <v-dialog v-model="modalExit" max-width="700px">
          <v-card class="mx-auto align-end">
            <v-card-title>Do you want to give up the creation of this notification?</v-card-title>
            <v-card-subtitle class="mt-1 mb-3">If you exit this page, this notification will be deleted.</v-card-subtitle>
            <v-card-actions>
              <v-spacer/>
              <v-btn
                color="blue darken-1"
                dark
                class="mr-1 mb-2"
                @click="nextRoute = null, modalExit = false"
              >
                <v-icon
                  size="large"
                  color="white"
                  class="ml-2"
                  left
                >
                   mdi-arrow-left
                </v-icon>
                Cancel
              </v-btn>
              <v-btn
                color="error"
                class="mr-5 mb-2"
                @click="deleteNotification"
              >
                <v-icon
                  size="large"
                  color="white"
                  class="ml-2"
                  left
                >
                  mdi-trash-can
                </v-icon>
                Exit and delete notification
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </template>
  </v-app>
</template>

<script>
  import notificationsApi from '@/services/api/notifications-api';
  import templatesApi from '@/services/api/templates-api';
  import userGroupsApi from '@/services/api/user-groups-api';

  import notificationType from '../../../data/icons';
  import countries from '../../../data/countries';

  export default {
    name: 'createNotification',

    data() {
      return {
        search: '',
        panel: [0],
        step: 1,
        radioGroup: 1,
        radioLabel: ['Incident Report (automatic)', 'Send to one or several group (manual, 9 groups MAX)'],
        userGroupsError: {
          usergroup: {
            userGroupId: null,
            title: null,
            type: null
          },
          totalUsers: 0
        },
        userGroups: [],
        headers: [
          { text: 'Group name', value: 'title' },
          { text: 'Number of users', value: 'usercount' }
        ],
        notification: {
          title: '',
          iconUrl: null,
          translations: [
            {
              title: null,
              description: null,
              langageId: 1
            }
          ],
          typeValue: [],
        },
        notificationType,
        templates: [],
        notificationTypeName: null,
        selectedTemplateId: null,
        langageId: 2,
        langageIdUsed: [],
        snackbar: false,
        snackbarError: false,
        sendLoading: false,
        modalTemplate: true,
        modalIncidentReport: false,
        modalSendNotification: false,
        modalExit: false,
        nextRoute: null,
        countDown: 3,
        isLoading: true,
        date: new Date().toISOString().substr(0, 10),
        time: this.getTime(),
        datePicker: false,
        timePicker: false,
        notificationSent: false,
        rules: [ value => !!value || 'Required.' ],
        isFormValid: false,

        // Selection du tableau
        singleSelect: false,
        rulesCountries: [ value => value.length > 0 || 'At least one country must be selected' ],
        countries,
        selectedCountries: []
      }
    },

    async mounted() {
      // Chargement des templates
      let dataTemplates = await templatesApi.getAllTemplates();
      this.templates = dataTemplates.data;

      // Chargement des user groups
      let dataUserGroups = await userGroupsApi.getAllUserGroups();
      this.userGroups = dataUserGroups.data;

      // Initialisation du tableau de pays sélectionné avec le premier pays du tableau de pays
      this.selectedCountries = this.countries;

      if (this.userGroups) {
        this.isLoading = false
      }
    },

    watch: {
      // On vérifie si tous les champs requis du formulaire sont renseignés
      notification: {
        handler (value) {
          this.isFormValid = !!(value.title && value.iconUrl && value.translations.every((translation) => translation.title && translation.description));
        },
        deep: true,
      },
      singleSelect: {
        handler() {
          if (this.singleSelect) {
            this.notification.typeValue = [];
          }
        }
      },
      'notification.typeValue': {
        handler(val, oldVal) {
          if (val && val.length > 9) {
            this.$nextTick(() => {
              this.notification.typeValue = oldVal;
            });
          }
        }
      }
    },

    beforeRouteLeave(to, from, next) {
      if (this.step === 2 && !this.notificationSent) {
        if (this.nextRoute) {
          next();
        } else {
          this.modalExit = true;
          this.nextRoute = to;
        }
      } else {
        next();
      }
    },

    computed: {
      getSelectedCountries () {
        return this.selectedCountries.length === this.countries.length
      },

      countrySelectAllIcon () {
        if (this.getSelectedCountries) return 'mdi-close-box'
        return 'mdi-checkbox-blank-outline'
      },
    },

    methods: {
      toggle () {
        this.$nextTick(() => {
          if (this.getSelectedCountries) {
            this.selectedCountries = [];
          } else {
            this.selectedCountries = this.countries.slice();
          }
        })
      },

      redirectToBack() {
        this.$router.back();
      },

      addTranslation() {
        this.isFormValid = false;

        this.notification.translations.push({
          title: null,
          description: null,
          langageId: this.langageId
        });

        this.langageIdUsed.push(this.langageId);
      },

      deleteTranslation(translationId) {
        this.notification.translations.splice(translationId, 1);
        this.langageIdUsed.splice(translationId, 1);
      },

      getLangageString(langageId) {
        switch (langageId) {
          case 1: return 'English (en)';
          case 2: return 'Danish (da)';
          case 3: return 'German (de)';
          case 4: return 'Spanish (es)';
          case 5: return 'French (fr)';
          case 6: return 'Hungarian (hu)';
          case 7: return 'Italian (it)';
          case 8: return 'Dutch (nl)';
          case 9: return 'Polish (po)';
          default: return 'English (en)';
        }
      },

      setNotificationUrl() {
        this.notification.iconUrl = `${process.env.VUE_APP_API_URL}/notification-icon/${this.notificationTypeName.toLowerCase()}`;
      },

      async getTemplate() {
        this.modalTemplate = false;

        let data = await templatesApi.getSingleTemplate(this.selectedTemplateId);

        // On récupère uniquement les données à modifier
        const iconUrl = data.data.iconUrl;
        this.notification.title = data.data.title;
        this.notification.iconUrl = iconUrl;  // L'URL en base est déjà l'URL complète de l'image, pas besoin de la modifier
        this.notification.translations = data.data.translations;

        // On ne garde que la partie de l'URL qui correspond au nom de l'icône
        this.notificationTypeName = iconUrl.substring(iconUrl.lastIndexOf('/') + 1);

        // On ajoute les langues déjà utilisés dans le tableau langageIdUsed
        for (let i = 0; i < this.notification.translations.length; i++) {
          this.langageIdUsed.push(this.notification.translations[i].langageId);
        }
      },

      skip() {
        this.modalTemplate = false;
        this.langageIdUsed.push(1);
      },

      async saveNotification() {
        // Transformer la liste des groupes en liste de string
        let flattenUserGroupsId = [];
        if (typeof this.notification.typeValue !== 'string') {
          // Transformer la liste des groupes en liste de string
          flattenUserGroupsId = this.notification.typeValue.map((group) => `${group.userGroupId}`);
        } else {
          flattenUserGroupsId = [this.notification.typeValue];
        }

        // On crée/met à jour la notification
        try {
          if (this.notificationId) {
            await notificationsApi.createNotification({
              ...this.notification,
              typeValue: flattenUserGroupsId,
            });
          } else {
            await notificationsApi.updateNotification(
              {
                ...this.notification,
                typeValue: flattenUserGroupsId,
              },
              this.notificationId,
            );
          }
        } catch (err) {
          this.snackbarError = true;
          return;
        }

        // On affiche le message indiquant que la notification à bien été créée
        this.snackbar = true;
        this.countDownTimer();

        // On redirige vers le listing des notifications après 3s
        setTimeout(() => {
          this.$router.push({ name: 'Notifications' });
        }, 3300)
      },

      async sendNotification() {
        this.sendLoading = true;
        this.modalIncidentReport = false;
        this.modalSendNotification = false;

        // Transformer la liste des groupes en liste de string
        let flattenUserGroupsId = [];
        if (typeof this.notification.typeValue !== 'string') {
          // Transformer la liste des groupes en liste de string
          flattenUserGroupsId = this.notification.typeValue.map((group) => `${group.userGroupId}`);
        } else {
          flattenUserGroupsId = [this.notification.typeValue];
        }

        // On enregistre la notification avec le typeValue
        await notificationsApi.updateNotification(
          {
            ...this.notification,
            typeValue: flattenUserGroupsId,
          },
          this.notificationId,
        )
          .then(async () => {
            try {
              await notificationsApi.sendNotification(this.notificationId);
            } catch {
              this.snackbarError = true;
              return;
            } finally {
              this.sendLoading = false;
            }

            // On affiche le message indiquant que le template à bien été crée
            this.snackbar = true;
            this.countDownTimer();

            // On indique que la notification a été envoyé
            this.notificationSent = true;

            // On redirige vers le listing des templates après 3s
            setTimeout(() => {
              this.$router.push({ name: 'Notifications' });
            }, 3300);
          })
          .catch(() => {
            this.snackbarError = true;
            this.sendLoading = false;
            return;
          });
      },

      async goToStep2() {
        this.step = 2;

        // On créer la notification
        const tmpNotif = await notificationsApi.createNotification(this.notification);

        // On récupère le "notificationId"
        this.notificationId = tmpNotif.data.notificationId;
      },

      async searchUsers() {
        // On vide le userGroupError
        this.userGroupsError = {};

        // On récupère des données de la date et de l'heure choisit
        const [year, month, day] = this.date.split('-');
        const [hour, minute] = this.time.split(':');

        const timeToSearch = new Date(Date.UTC(year, month - 1, day, hour, minute)).toISOString();

        // Récupération du userGroup avec les users qui matches
        try {
          const userGroupsError = await userGroupsApi.getUserGroupsError(timeToSearch, this.selectedCountries);
          this.userGroupsError = userGroupsError.data;
          this.modalIncidentReport = true;
        } catch(error) {
          this.modalIncidentReport = true;
        }
      },

      async deleteNotification() {
        this.modalExit = false;

        // On supprime la notification
        await notificationsApi.deleteNotification(this.notificationId);

        // On redirige vers la page shouaité
        this.$router.push(this.nextRoute);
      },

      async cancel() {
        this.step = 1

        // On supprime la notification
        await notificationsApi.deleteNotification(this.notificationId);
      },

      getTime() {
        // Chargement de l'heure
        const d = new Date();

        const hours = `00${d.getHours()}`.slice(-2);
        const minutes = `00${d.getMinutes()}`.slice(-2);

        return `${hours}:${minutes}`;
      },

      countDownTimer() {
        if (this.countDown > 0) {
          setTimeout(() => {
            this.countDown -= 1;
            this.countDownTimer();
          }, 1000)
        }
      },

      async createNotification(data) {
        // On créer la notification
        const notificationCreated = await notificationsApi.createNotification(data);
        // On filtre les notifications récupérer pour prendre la dernière (celle qui vient d'être créée)
        return notificationCreated.data.notificationId;
      }
    }
  };
</script>
